import { useInView } from '@/hooks/useInView';

const brandIdentity = [
  {
    label: '正式名稱',
    value: '山頂視角文化事業有限公司',
    icon: 'ri-building-4-line',
  },
  {
    label: '網站簡稱',
    value: '山頂視角',
    icon: 'ri-window-line',
  },
  {
    label: '英文名稱',
    value: 'PEAKVIEW',
    icon: 'ri-global-line',
  },
  {
    label: '出版定位',
    value: '商業理財、政治與以古諷今類人文社科書籍',
    icon: 'ri-book-3-line',
  },
];

const publishingDirections = [
  {
    title: '商業理財',
    description: '以商業觀念、經濟思考、管理與理財相關主題為出版內容。',
    icon: 'ri-line-chart-line',
  },
  {
    title: '政治與社會觀察',
    description: '以政治、制度、社會現象與相關議題為閱讀方向。',
    icon: 'ri-scales-3-line',
  },
  {
    title: '以古諷今與人文社科',
    description: '透過歷史人物、事件與思想內容，連結當代社會及人文議題。',
    icon: 'ri-history-line',
  },
];

const brandPrinciples = [
  {
    num: '01',
    title: '不只看事件，也看結構',
    description: '透過制度、權力、利益與社會條件，理解事件背後的形成原因。',
    icon: 'ri-stack-line',
  },
  {
    num: '02',
    title: '不只讀歷史，也讀現代',
    description: '以歷史事件、人物與制度作為理解現代社會的參照。',
    icon: 'ri-history-line',
  },
  {
    num: '03',
    title: '不只談商業，也談判斷',
    description: '從商業模式、風險、制度與選擇，理解市場和決策背後的邏輯。',
    icon: 'ri-scales-3-line',
  },
  {
    num: '04',
    title: '不只提供知識，也建立視角',
    description: '協助讀者將零散資訊整理為可長期使用的理解框架。',
    icon: 'ri-compass-3-line',
  },
];

const readerTags = [
  '關注商業理財的讀者',
  '關注政治、歷史與社會變化',
  '喜歡以古鑑今、以史觀今',
  '想理解制度與權力結構',
  '對宏觀趨勢有興趣',
  '喜歡非虛構知識型閱讀',
];

export default function BrandIntroSection() {
  const { ref: refHeader, inView: inViewHeader } = useInView();
  const { ref: refIdentity, inView: inViewIdentity } = useInView();
  const { ref: refDirections, inView: inViewDirections } = useInView();
  const { ref: refRelation, inView: inViewRelation } = useInView();
  const { ref: refCatalog, inView: inViewCatalog } = useInView();
  const { ref: refPrinciples, inView: inViewPrinciples } = useInView();
  const { ref: refReaders, inView: inViewReaders } = useInView();

  const anim = (inView: boolean, delay = 0) =>
    `transition-all duration-700 ease-out ${delay > 0 ? `delay-[${delay}ms]` : ''} ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`;

  return (
    <section className="py-20 md:py-28 px-6 md:px-10 bg-accent-50">
      <div className="max-w-6xl mx-auto w-full">

        {/* ───── Section Header ───── */}
        <div ref={refHeader} className={`max-w-3xl mx-auto text-center ${anim(inViewHeader)}`}>
          <p className="text-base font-label tracking-widest text-foreground-500 mb-3 uppercase">Brand</p>
          <h2 className="font-heading text-3xl md:text-5xl font-bold text-foreground-950 leading-tight mb-3">
            認識山頂視角
          </h2>
          <p className="text-base md:text-lg text-foreground-500 leading-relaxed">
            商業思考、政治觀察與人文視角
          </p>
        </div>

        {/* ───── 品牌身分 ───── */}
        <div ref={refIdentity} className={`mt-14 ${anim(inViewIdentity)}`}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {brandIdentity.map((item, i) => (
              <div
                key={item.label}
                className="bg-background-50 rounded-lg p-6 md:p-7 hover:bg-background-100 transition-colors duration-300"
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <div className="w-10 h-10 flex items-center justify-center bg-accent-100 rounded-full mb-4">
                  <i className={`${item.icon} text-base text-accent-700`}></i>
                </div>
                <p className="text-xs font-label tracking-wider text-foreground-400 uppercase mb-2">{item.label}</p>
                <p className="text-base text-foreground-950 font-medium leading-relaxed">{item.value}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ───── 品牌介紹文字 ───── */}
        <div className="mt-10 max-w-3xl mx-auto">
          <p className="text-base text-foreground-500 leading-relaxed">
            山頂視角以商業理財、政治與以古諷今類人文社科書籍為主要出版方向，透過不同時代、社會議題與知識觀點，提供讀者理解商業、政治及人文議題的閱讀內容。網站呈現山頂視角的品牌定位、2026 最新發行書籍與相關出版資訊。
          </p>
        </div>

        {/* ───── 三大出版方向 ───── */}
        <div ref={refDirections} className={`mt-16 ${anim(inViewDirections)}`}>
          <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 text-center mb-2">
            出版方向
          </h3>
          <p className="text-center text-foreground-500 text-base mb-10 max-w-xl mx-auto leading-relaxed">
            山頂視角目前以三個主要方向規劃出版內容。
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {publishingDirections.map((dir, i) => (
              <div
                key={dir.title}
                className="group bg-background-50 rounded-lg p-6 md:p-7 hover:bg-background-100 transition-all duration-300"
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <div className="w-11 h-11 flex items-center justify-center bg-accent-100 rounded-lg mb-4 group-hover:bg-accent-200 transition-colors">
                  <i className={`${dir.icon} text-lg text-accent-700`}></i>
                </div>
                <h4 className="font-heading text-base md:text-lg font-semibold text-foreground-950 mb-2">
                  {dir.title}
                </h4>
                <p className="text-base text-foreground-500 leading-relaxed">
                  {dir.description}
                </p>
              </div>
            ))}
          </div>
          <p className="mt-8 text-sm text-foreground-400 max-w-2xl mx-auto text-center leading-relaxed">
            書籍內容僅屬於出版與閱讀資訊，不代表提供專業投資、金融、政治或法律服務。
          </p>
        </div>

        {/* ───── 品牌與崧博出版 ───── */}
        <div ref={refRelation} className={`mt-16 pt-12 border-t border-foreground-200/40 ${anim(inViewRelation)}`}>
          <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 text-center mb-6">
            品牌與崧博出版
          </h3>
          <div className="max-w-2xl mx-auto text-center">
            <p className="text-base text-foreground-700 font-medium leading-relaxed mb-3">
              本品牌委託崧博出版經銷，官方網站由崧博出版製作。
            </p>
            <p className="text-sm text-foreground-400 leading-relaxed">
              委託經銷與網站製作，不代表山頂視角是崧博旗下品牌、子品牌或自有品牌。
            </p>
          </div>
        </div>

        {/* ───── 書目查詢 CTA ───── */}
        <div ref={refCatalog} className={`mt-12 ${anim(inViewCatalog)}`}>
          <div className="max-w-xl mx-auto text-center bg-background-50 rounded-lg p-8 md:p-10 border border-background-200/70">
            <div className="w-12 h-12 mx-auto mb-5 flex items-center justify-center rounded-full bg-accent-100">
              <i className="ri-book-open-line text-lg text-accent-700"></i>
            </div>
            <h3 className="font-heading text-lg md:text-xl font-bold text-foreground-950 mb-2">
              查詢完整書目資訊
            </h3>
            <p className="text-base text-foreground-500 leading-relaxed mb-6">
              完整書名、作者與出版資訊，請前往崧博書籍資料庫查詢。
            </p>
            <a
              href="https://catalog.sonbook.net/"
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white font-label font-medium text-base rounded-full hover:bg-primary-600 transition-all duration-300 whitespace-nowrap"
            >
              前往書籍資料庫
              <i className="ri-external-link-line"></i>
            </a>
          </div>
        </div>

        {/* ───── 品牌理念 ───── */}
        <div ref={refPrinciples} className={`mt-16 pt-12 border-t border-foreground-200/40 ${anim(inViewPrinciples)}`}>
          <h3 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-10">
            品牌理念
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {brandPrinciples.map((principle, i) => (
              <div
                key={principle.num}
                className="group bg-background-50 rounded-lg p-6 md:p-7 hover:bg-background-100 transition-all duration-300"
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <div className="flex items-center justify-between mb-5">
                  <span className="font-heading text-4xl md:text-5xl font-bold text-foreground-200 group-hover:text-accent-200 transition-colors">
                    {principle.num}
                  </span>
                  <span className="w-9 h-9 flex items-center justify-center rounded-full bg-accent-100">
                    <i className={`${principle.icon} text-sm text-accent-700`}></i>
                  </span>
                </div>
                <h4 className="font-heading text-base md:text-lg font-semibold text-foreground-950 mb-2">
                  {principle.title}
                </h4>
                <p className="text-base text-foreground-500 leading-relaxed">
                  {principle.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* ───── 適合哪些讀者 ───── */}
        <div ref={refReaders} className={`mt-14 ${anim(inViewReaders)}`}>
          <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 text-center mb-4">
            山頂視角適合哪些讀者？
          </h3>
          <p className="text-center text-foreground-500 mb-8 max-w-xl mx-auto leading-relaxed text-base">
            適合關注不同主題與觀點，重視書籍資訊清楚度的讀者。
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            {readerTags.map((item) => (
              <span
                key={item}
                className="px-4 py-2 text-sm bg-secondary-100 text-secondary-800 rounded-full whitespace-nowrap"
              >
                {item}
              </span>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
}